import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastService } from '@teorema/patron';
import {
  BulkDiscountLimit,
  BulkMaintenanceBwUser,
  BulkRelationMode,
  BulkRelationOperation,
  BwUserBulkPayload,
  BwUserBulkResult,
} from 'src/app/models/bw-user-bulk-maintenance.model';
import { FilterField } from 'src/app/models/filter-field.model';
import { FilterOperator, FindOptions } from 'src/app/models/find-options.model';
import { BwUserService, utils } from 'src/app/shared';

/**
 * Nome de cada campo editável. É a chave do Set `enabledFields`: um campo só entra no
 * payload se estiver nesse Set, e é isso que garante que a manutenção em massa altere
 * SOMENTE o que o usuário marcou — inclusive quando o valor escolhido é vazio/nulo
 * (limpar vínculo, zerar metas), caso que um "campo vazio = não altera" não conseguiria
 * expressar.
 *
 * Mesma mecânica do ticket-bulk-maintenance do v3ndor, que é o padrão do repo para
 * manutenção em massa.
 */
export type BwUserBulkField =
  // Acesso
  | 'bw_user_profiles'
  | 'active'
  | 'show_prices'
  // Configurações específicas
  | 'read_all_customers'
  | 'emit_nfe'
  | 'order_counterpassword'
  | 'itemFilter'
  | 'itemExclude'
  | 'discountLimit'
  // Licenças e metas
  | 'licenses_web'
  | 'licenses_mobile'
  | 'daily_sales_goals'
  | 'monthly_sales_goals'
  // Templates
  | 'reports_charts'
  // Vínculos
  | 'seller_id'
  | 'items'
  | 'stock_locales'
  | 'price_tables'
  | 'payment_conditions'
  | 'companies'
  | 'departments'
  // Marcações de pivot
  | 'main_price_table_id'
  | 'main_payment_condition_id'
  | 'main_company_id'
  | 'main_stock_locale_id'
  | 'counting_stock_locale_ids'
  | 'department_manager';

type GroupKey = 'access' | 'specific' | 'licenses' | 'templates' | 'relations' | 'pivots';

/**
 * Os grupos colapsáveis do modal, declarados como dado.
 *
 * Ter a lista de campos aqui é o que permite (a) contar quantos estão marcados no cabeçalho e
 * (b) esconder o grupo inteiro quando nenhum dos seus campos se aplica à seleção — em vez de
 * mostrar um cartão com todos os campos desabilitados.
 */
const GROUPS: { key: GroupKey; title: string; fields: BwUserBulkField[] }[] = [
  {
    key: 'access',
    title: 'Acesso',
    fields: ['bw_user_profiles', 'active', 'show_prices'],
  },
  {
    key: 'specific',
    title: 'Configurações específicas',
    fields: [
      'read_all_customers',
      'emit_nfe',
      'order_counterpassword',
      'itemFilter',
      'itemExclude',
      'discountLimit',
    ],
  },
  {
    key: 'licenses',
    title: 'Licenças por plataforma e Metas de Vendas',
    fields: ['licenses_web', 'licenses_mobile', 'daily_sales_goals', 'monthly_sales_goals'],
  },
  {
    key: 'templates',
    title: 'Tipos de Templates',
    fields: ['reports_charts'],
  },
  {
    key: 'relations',
    title: 'Vínculos',
    fields: [
      'seller_id',
      'items',
      'stock_locales',
      'price_tables',
      'payment_conditions',
      'companies',
      'departments',
    ],
  },
  {
    key: 'pivots',
    title: 'Padrões e marcações',
    fields: [
      'main_price_table_id',
      'main_payment_condition_id',
      'main_company_id',
      'main_stock_locale_id',
      'counting_stock_locale_ids',
      'department_manager',
    ],
  },
];

/** Campos que só existem para acessos com cadastro de usuário no ERP (is_user). */
const ERP_FIELDS: BwUserBulkField[] = [
  'read_all_customers',
  'emit_nfe',
  'order_counterpassword',
  'itemFilter',
  'itemExclude',
  'discountLimit',
  'licenses_web',
  'licenses_mobile',
  'daily_sales_goals',
  'monthly_sales_goals',
  'reports_charts',
  'seller_id',
  'items',
  'stock_locales',
  'price_tables',
  'payment_conditions',
  'companies',
  'departments',
  'main_price_table_id',
  'main_payment_condition_id',
  'main_company_id',
  'main_stock_locale_id',
  'counting_stock_locale_ids',
  'department_manager',
];

/** Acima disso o processamento sequencial no backend fica perceptível. */
const LARGE_BATCH_THRESHOLD = 50;

/**
 * Valor inicial de cada controle, em um só lugar.
 *
 * O form é montado a partir daqui e o `resetState` volta para cá: com duas listas separadas,
 * um campo novo esquecido no reset manteria o valor da abertura anterior do modal — e o
 * componente inteiro existe para garantir que só o que foi marcado agora seja aplicado.
 */
const DEFAULT_FORM_VALUES = {
  // Acesso
  bw_user_profiles: null,
  bw_user_profiles_mode: 'append',
  active: true,
  show_prices: 1,
  // Configurações específicas
  read_all_customers: 0,
  emit_nfe: 0,
  order_counterpassword: 0,
  itemFilter: false,
  itemExclude: false,
  // Licenças e metas
  licenses_web: null,
  licenses_mobile: null,
  daily_sales_goals: null,
  monthly_sales_goals: null,
  // Templates
  reports_charts: null,
  reports_charts_mode: 'append',
  // Vínculos
  seller_id: null,
  items: null,
  items_mode: 'append',
  stock_locales: null,
  stock_locales_mode: 'append',
  price_tables: null,
  price_tables_mode: 'append',
  payment_conditions: null,
  payment_conditions_mode: 'append',
  companies: null,
  companies_mode: 'append',
  departments: null,
  departments_mode: 'append',
  // Marcações de pivot
  main_price_table_id: null,
  main_payment_condition_id: null,
  main_company_id: null,
  main_stock_locale_id: null,
  counting_stock_locale_ids: null,
  department_manager: false,
};

/** Acesso já nasce aberto porque vale para qualquer tipo de cadastro. */
const DEFAULT_OPEN_GROUPS: Record<GroupKey, boolean> = {
  access: true,
  specific: false,
  licenses: false,
  templates: false,
  relations: false,
  pivots: false,
};

@Component({
  selector: 'app-bw-user-bulk-maintenance',
  templateUrl: './bw-user-bulk-maintenance.component.html',
  styleUrl: './bw-user-bulk-maintenance.component.scss',
  standalone: false,
})
export class BwUserBulkMaintenanceComponent implements OnChanges {
  @Input() visible = false;
  /** Acessos selecionados na listagem, exibidos na coluna da esquerda. */
  @Input() bwUsers: BulkMaintenanceBwUser[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emitido só quando o lote é aplicado (nunca no cancelar). */
  @Output() done = new EventEmitter<BwUserBulkResult>();
  /** Acesso removido da seleção dentro do modal, para o pai desmarcá-lo também. */
  @Output() bwUserRemoved = new EventEmitter<string>();

  readonly largeBatchThreshold = LARGE_BATCH_THRESHOLD;
  readonly relationModes: { value: BulkRelationMode; label: string }[] = [
    { value: 'append', label: 'Adicionar aos existentes' },
    { value: 'replace', label: 'Substituir todos' },
    { value: 'remove', label: 'Remover os selecionados' },
  ];

  /**
   * Vínculos de múltipla seleção. Os modelName e bindLabel são os mesmos que a tela de
   * detalhe do usuário usa, para que as opções oferecidas sejam idênticas.
   */
  readonly relationFields: {
    field: BwUserBulkField;
    label: string;
    modelName: string;
    bindLabel: string;
    activeOnly?: boolean;
    help?: string;
  }[] = [
    { field: 'items', label: 'Itens', modelName: 'items', bindLabel: '_description' },
    {
      field: 'stock_locales',
      label: 'Locais de estoque',
      modelName: 'stock-locales',
      bindLabel: '_name',
    },
    {
      field: 'price_tables',
      label: 'Tabelas de preço',
      modelName: 'price-tables',
      bindLabel: '_description',
    },
    {
      field: 'payment_conditions',
      label: 'Condições de pagamento',
      modelName: 'payment-conditions',
      bindLabel: '_name',
      activeOnly: true,
    },
    { field: 'companies', label: 'Empresas', modelName: 'companies', bindLabel: '_name' },
    {
      field: 'departments',
      label: 'Departamentos',
      modelName: 'departments',
      bindLabel: '_name',
      activeOnly: true,
    },
  ];

  /** Marcações de "padrão" — o registro precisa já estar vinculado ao usuário. */
  readonly mainPivotFields: {
    field: BwUserBulkField;
    label: string;
    modelName: string;
    bindLabel: string;
  }[] = [
    {
      field: 'main_price_table_id',
      label: 'Tabela de preço — Padrão',
      modelName: 'price-tables',
      bindLabel: '_description',
    },
    {
      field: 'main_payment_condition_id',
      label: 'Condição de pagamento — Padrão',
      modelName: 'payment-conditions',
      bindLabel: '_name',
    },
    {
      field: 'main_company_id',
      label: 'Empresa — Padrão',
      modelName: 'companies',
      bindLabel: '_name',
    },
    {
      field: 'main_stock_locale_id',
      label: 'Local de estoque — Padrão',
      modelName: 'stock-locales',
      bindLabel: '_name',
    },
  ];

  readonly groups = GROUPS;

  form: FormGroup;
  saving = false;

  /** Campos marcados para alteração. Nada fora daqui entra no payload. */
  enabledFields = new Set<BwUserBulkField>();

  /** Grupos abertos. Cópia: `toggleGroup` altera este objeto. */
  openGroups: Record<GroupKey, boolean> = { ...DEFAULT_OPEN_GROUPS };

  discountLimits: BulkDiscountLimit[] = [];

  /** Mensagem de falha por acesso, preenchida quando o lote volta parcial. */
  failures = new Map<string, string>();

  profilesFindOptions: FindOptions = { orderBy: 'name' };
  defaultFindOptions: FindOptions = {};
  /** Mesmo filtro oculto que a tela de detalhe aplica nessas buscas. */
  activeOnlyFindOptions: FindOptions = {
    filters: [new FilterField({ label: 'Ativo', name: 'active', value: true, visible: false })],
  };

  patronUtils = utils;

  constructor(
    private formBuilder: FormBuilder,
    private service: BwUserService,
    private toastService: ToastService
  ) {
    this.form = this.formBuilder.group(DEFAULT_FORM_VALUES);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetState();
    }

    if (changes['bwUsers']) {
      this.updateProfileTypeFilter();
      this.dropUnavailableFields();
    }
  }

  /**
   * A seleção muda com o modal aberto (o ✕ de cada card, ou a redução após uma falha
   * parcial), e um campo marcado pode deixar de ser aplicável — ele desaparece da tela, mas
   * continuaria no Set. Sem esta limpeza, remover o único cliente da seleção ainda gravaria
   * "Mostrar preços" nos demais.
   */
  private dropUnavailableFields(): void {
    for (const field of Array.from(this.enabledFields)) {
      if (!this.isAvailable(field)) {
        this.enabledFields.delete(field);
      }
    }
  }

  // ─── Composição da seleção ───────────────────────────────────────────────

  /** Os campos do ERP só se aplicam se TODOS os selecionados têm cadastro de usuário. */
  get allAreUsers(): boolean {
    return this.bwUsers.length > 0 && this.bwUsers.every(bwUser => !!bwUser.is_user);
  }

  get anyIsCustomer(): boolean {
    return this.bwUsers.some(bwUser => !!bwUser.is_customer);
  }

  /**
   * Interseção dos tipos de cadastro da seleção.
   *
   * A tela unitária oferece os perfis compatíveis com os tipos daquele usuário; em lote a
   * interseção é o único conjunto seguro — oferecer a união deixaria escolher um perfil de
   * usuário para um acesso que é só cliente, e o backend recusaria o item.
   */
  get commonProfileTypes(): string[] {
    if (!this.bwUsers.length) return [];

    const types = ['U', 'C', 'S'];
    const flagOf: Record<string, (bwUser: BulkMaintenanceBwUser) => boolean> = {
      U: bwUser => !!bwUser.is_user,
      C: bwUser => !!bwUser.is_customer,
      S: bwUser => !!bwUser.is_supplier,
    };

    return types.filter(type => this.bwUsers.every(bwUser => flagOf[type](bwUser)));
  }

  get profilesDisabledReason(): string | null {
    if (!this.commonProfileTypes.length) {
      return 'Os selecionados não são todos do mesmo tipo (usuário, cliente ou fornecedor) — não há perfil válido para todos.';
    }
    return null;
  }

  get erpDisabledReason(): string | null {
    if (!this.allAreUsers) {
      return 'A seleção contém acessos que não são de usuário, então só os campos de acesso podem ser alterados em lote.';
    }
    return null;
  }

  // ─── Grupos ──────────────────────────────────────────────────────────────

  /**
   * Um grupo só aparece se algum campo dele se aplica à seleção. Numa seleção que mistura
   * acessos com e sem cadastro no ERP sobra apenas "Acesso" — melhor do que oferecer cinco
   * cartões inteiramente desabilitados.
   */
  isGroupVisible(group: { fields: BwUserBulkField[] }): boolean {
    return group.fields.some(field => this.isAvailable(field));
  }

  get visibleGroups(): { key: GroupKey; title: string; fields: BwUserBulkField[] }[] {
    return this.groups.filter(group => this.isGroupVisible(group));
  }

  toggleGroup(group: GroupKey): void {
    this.openGroups[group] = !this.openGroups[group];
  }

  /** Quantos campos marcados o grupo tem — mostrado no cabeçalho recolhido. */
  countEnabled(fields: BwUserBulkField[]): number {
    return fields.filter(field => this.enabledFields.has(field)).length;
  }

  // ─── Marcação de campos ──────────────────────────────────────────────────

  findOptionsFor(relation: { activeOnly?: boolean }): FindOptions {
    return relation.activeOnly ? this.activeOnlyFindOptions : this.defaultFindOptions;
  }

  isEnabled(field: BwUserBulkField): boolean {
    return this.enabledFields.has(field);
  }

  /**
   * Marcado E aplicável — é o que decide o que entra no payload.
   *
   * `isEnabled` sozinho não basta: a seleção muda com o modal aberto, e um campo marcado
   * pode ficar indisponível. O dropUnavailableFields já limpa o Set nesse caso; esta
   * checagem é a segunda barreira, para a montagem do payload não depender dela.
   */
  private isActive(field: BwUserBulkField): boolean {
    return this.enabledFields.has(field) && this.isAvailable(field);
  }

  isAvailable(field: BwUserBulkField): boolean {
    if (field === 'bw_user_profiles') return this.commonProfileTypes.length > 0;
    if (field === 'show_prices') return this.anyIsCustomer;
    // Com o sync do ERP ativo o vendedor vem do Fenix e seria sobrescrito na próxima
    // sincronização — a tela de detalhe bloqueia a troca pelo mesmo motivo.
    if (field === 'seller_id') return this.allAreUsers && !utils.isActiveSync();
    if (ERP_FIELDS.includes(field)) return this.allAreUsers;
    return true;
  }

  toggleField(field: BwUserBulkField, checked: boolean): void {
    if (checked && !this.isAvailable(field)) return;

    if (checked) {
      this.enabledFields.add(field);
    } else {
      this.enabledFields.delete(field);
    }

    // Bloqueio por filtro de itens só faz sentido com o filtro de itens ligado, mesma
    // regra da tela de detalhe.
    if (field === 'itemFilter' && !checked) {
      this.enabledFields.delete('itemExclude');
    }
  }

  get hasAnyChange(): boolean {
    return this.enabledFields.size > 0;
  }

  /**
   * Marcar um "padrão" sem escolher o registro não tem efeito nenhum no backend, então o
   * Aplicar fica bloqueado em vez de mentir que fez algo.
   */
  get pendingMainField(): string | null {
    for (const pivot of this.mainPivotFields) {
      if (this.isActive(pivot.field) && !this.form.value[pivot.field]) {
        return pivot.label;
      }
    }
    return null;
  }

  // ─── Limites de desconto ─────────────────────────────────────────────────

  addDiscountLimit(): void {
    this.discountLimits.push({ minValue: null, maxValue: null, discount: null });
  }

  removeDiscountLimit(index: number): void {
    this.discountLimits.splice(index, 1);
  }

  // ─── Seleção ─────────────────────────────────────────────────────────────

  removeBwUser(bwUser: BulkMaintenanceBwUser): void {
    this.failures.delete(bwUser.id);
    this.bwUserRemoved.emit(bwUser.id);
  }

  failureOf(bwUser: BulkMaintenanceBwUser): string | undefined {
    return this.failures.get(bwUser.id);
  }

  // ─── Aplicar ─────────────────────────────────────────────────────────────

  onCancel(): void {
    // Cancelar nunca descarta a seleção: o usuário pode querer revisar e reabrir.
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async onApply(): Promise<void> {
    if (!this.hasAnyChange || !this.bwUsers.length || this.saving) return;

    const payload = this.buildPayload();

    if (!payload) {
      this.toastService.show({ color: 'warn', message: 'Nenhuma alteração a aplicar' });
      return;
    }

    this.saving = true;
    this.failures.clear();

    const result = await this.service.bulkMaintenance(payload).catch(err => {
      if (!err?.messageShown) {
        this.toastService.show({
          color: 'error',
          message: err?.error?.message || 'Houve um erro ao aplicar a manutenção',
        });
      }
      return null;
    });

    this.saving = false;

    if (!result) return;

    if (result.failed > 0) {
      for (const item of result.results) {
        if (!item.ok) {
          this.failures.set(item.id, item.message || 'Falha ao atualizar');
        }
      }

      this.toastService.show({
        color: 'warn',
        message: `Atualização parcial — ${result.updated} de ${result.total} usuário(s) atualizados`,
      });

      // O modal segue aberto e o pai reduz a seleção aos que falharam: nem toda escrita do
      // lote é idempotente, então reaplicar em todos poderia duplicar efeito.
      this.done.emit(result);
      return;
    }

    this.toastService.show({ color: 'success', message: 'Manutenção aplicada' });
    this.done.emit(result);
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Monta o payload a partir de `enabledFields`. Devolve null quando não há nada a
   * aplicar, para que `onApply` avise em vez de chamar a API à toa.
   */
  buildPayload(): BwUserBulkPayload | null {
    const value = this.form.value;
    const payload: BwUserBulkPayload = { bw_user_ids: this.bwUsers.map(bwUser => bwUser.id) };

    // Acesso
    const access: NonNullable<BwUserBulkPayload['access']> = {};
    if (this.isActive('bw_user_profiles')) {
      access.bw_user_profiles = this.operationOf(
        value.bw_user_profiles_mode,
        value.bw_user_profiles
      );
    }
    if (this.isActive('active')) access.active = !!value.active;
    if (this.isActive('show_prices')) access.show_prices = !!value.show_prices;
    if (Object.keys(access).length) payload.access = access;

    // Campos do usuário do ERP
    const userFields: NonNullable<BwUserBulkPayload['user_fields']> = {};
    if (this.isActive('read_all_customers'))
      userFields.read_all_customers = !!value.read_all_customers;
    if (this.isActive('emit_nfe')) userFields.emit_nfe = !!value.emit_nfe;
    if (this.isActive('order_counterpassword')) {
      userFields.order_counterpassword = !!value.order_counterpassword;
    }
    if (this.isActive('licenses_web'))
      userFields.licenses_web = this.numberOrNull(value.licenses_web);
    if (this.isActive('licenses_mobile')) {
      userFields.licenses_mobile = this.numberOrNull(value.licenses_mobile);
    }
    if (this.isActive('daily_sales_goals')) {
      userFields.daily_sales_goals = this.numberOrNull(value.daily_sales_goals);
    }
    if (this.isActive('monthly_sales_goals')) {
      userFields.monthly_sales_goals = this.numberOrNull(value.monthly_sales_goals);
    }
    if (this.isActive('reports_charts')) {
      userFields.reports_charts = this.operationOf(value.reports_charts_mode, value.reports_charts);
    }
    if (Object.keys(userFields).length) payload.user_fields = userFields;

    // user.config
    const userConfig: NonNullable<BwUserBulkPayload['user_config']> = {};
    if (this.isActive('itemFilter')) userConfig.itemFilter = !!value.itemFilter;
    if (this.isActive('itemExclude')) userConfig.itemExclude = !!value.itemExclude;
    if (this.isActive('discountLimit')) {
      userConfig.discountLimit = this.discountLimits.filter(rule => rule.discount != null);
    }
    if (Object.keys(userConfig).length) payload.user_config = userConfig;

    // Vínculos
    const relations: NonNullable<BwUserBulkPayload['relations']> = {};
    if (this.isActive('seller_id')) relations.seller_id = value.seller_id || null;
    for (const field of [
      'items',
      'stock_locales',
      'price_tables',
      'payment_conditions',
      'companies',
      'departments',
    ] as const) {
      if (this.isActive(field)) {
        relations[field] = this.operationOf(value[`${field}_mode`], value[field]);
      }
    }
    if (Object.keys(relations).length) payload.relations = relations;

    // Marcações de pivot
    const pivots: NonNullable<BwUserBulkPayload['pivots']> = {};
    for (const field of [
      'main_price_table_id',
      'main_payment_condition_id',
      'main_company_id',
      'main_stock_locale_id',
    ] as const) {
      if (this.isActive(field) && value[field]) pivots[field] = value[field];
    }
    if (this.isActive('counting_stock_locale_ids')) {
      pivots.counting_stock_locale_ids = value.counting_stock_locale_ids || [];
    }
    if (this.isActive('department_manager')) pivots.department_manager = !!value.department_manager;
    if (Object.keys(pivots).length) payload.pivots = pivots;

    const hasEffect =
      !!payload.access ||
      !!payload.user_fields ||
      !!payload.user_config ||
      !!payload.relations ||
      !!payload.pivots;

    return hasEffect ? payload : null;
  }

  private operationOf(mode: BulkRelationMode, ids: string[] | null): BulkRelationOperation {
    return { mode: mode || 'append', ids: ids ?? [] };
  }

  private numberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private resetState(): void {
    this.enabledFields.clear();
    this.failures.clear();
    this.discountLimits = [];
    this.saving = false;
    this.openGroups = { ...DEFAULT_OPEN_GROUPS };
    this.form.reset({ ...DEFAULT_FORM_VALUES });
    this.updateProfileTypeFilter();
  }

  /** Restringe as opções de perfil aos tipos comuns a toda a seleção. */
  private updateProfileTypeFilter(): void {
    const types = this.commonProfileTypes;

    this.profilesFindOptions = {
      orderBy: 'name',
      filters: types.length
        ? [
            new FilterField({
              label: 'Tipo',
              name: 'type',
              type: 'text',
              multiple: true,
              operator: FilterOperator.in,
              value: [...types],
            }),
          ]
        : [],
    };
  }
}
